import React, { useContext, useState } from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import {
  BorderColorOutlined,
  AddCircle,
  ArrowDropDown,
  BusinessOutlined,
  DashboardOutlined,
  PersonOutline,
} from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';

import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { TooltipMenu } from '../../../../../common/tooltip-menu';
import {
  DashboardView,
  ManagementDashboardContext,
} from '../../../../program-manager/management-dashboard/ManagementDashboardProvider';

const useStyles = makeStyles(() =>
  createStyles({
    button: {
      height: 38,
      borderRadius: 6,
      padding: '6px 12px',
      marginLeft: '0.75rem',
      textTransform: 'capitalize',
    },
    text: {
      marginLeft: '4px',
      fontWeight: 600,
      fontSize: '13px',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },
  })
);

const VIEW_LABELS: Record<DashboardView, string> = {
  mine: 'Mine',
  organisation: 'Organisation',
};

const VIEW_OPTIONS: { value: DashboardView; label: string; icon: React.ReactNode }[] = [
  { value: 'mine', label: VIEW_LABELS.mine, icon: <PersonOutline style={{ fontSize: '16px' }} /> },
  { value: 'organisation', label: VIEW_LABELS.organisation, icon: <BusinessOutlined style={{ fontSize: '16px' }} /> },
];

export const DashboardToolbar = () => {
  const {
    onCancelChangeLayout,
    onSubmitLayout,
    editLayout,
    setEditLayout,
    setShowEditWidget,
    view,
    setView,
    canEdit,
    personalDashboardsDisabled,
  } = useContext(ManagementDashboardContext);

  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const { theme, isSm } = useCampfireTheme();
  const classes = useStyles();
  return (
    <Box
      display='flex'
      flexDirection='row'
      width='100%'
      justifyContent='flex-end'
      alignItems='center'
      pb={isSm ? 0 : '14px'}
    >
      {!editLayout && !personalDashboardsDisabled && (
        <TooltipMenu
          open={viewMenuOpen}
          onClose={() => setViewMenuOpen(false)}
          title={
            <Box display='flex' flexDirection='column' alignItems='flex-start' style={{ padding: '0.5rem' }}>
              {VIEW_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  onClick={() => {
                    setView(option.value);
                    setViewMenuOpen(false);
                  }}
                  style={{
                    color: theme.alert.blue.extraLight,
                    display: 'flex',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    alignItems: 'center',
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    minWidth: 'auto',
                  }}
                  startIcon={option.icon}
                >
                  <Typography style={{ fontSize: '14px', fontWeight: option.value === view ? 700 : 500 }}>
                    {option.label}
                  </Typography>
                </Button>
              ))}
            </Box>
          }
        >
          <Button
            variant={'outlined'}
            startIcon={<DashboardOutlined style={{ marginTop: '1px', fontSize: '16px' }} />}
            endIcon={<ArrowDropDown />}
            className={classes.button}
            style={{
              backgroundColor: '#FCFCFC',
              border: '1px solid #DCDCDC',
              color: theme.color.grey.neutral300,
            }}
            onClick={() => setViewMenuOpen(!viewMenuOpen)}
          >
            <Typography className={classes.text}>{`View: ${VIEW_LABELS[view]}`}</Typography>
          </Button>
        </TooltipMenu>
      )}
      {!canEdit ? null : editLayout ? (
        <React.Fragment>
          <Button
            variant={'outlined'}
            className={classes.button}
            style={{
              backgroundColor: '#FCFCFC',
              border: '1px solid #DCDCDC',
              color: theme.color.grey.neutral300,
            }}
            onClick={onCancelChangeLayout}
          >
            <Typography className={classes.text}>Cancel</Typography>
          </Button>
          <Button
            variant={'contained'}
            className={classes.button}
            style={{
              backgroundColor: theme.color.secondary.main900,
              border: '1px solid transparent',
              color: '#FCFCFC',
            }}
            onClick={onSubmitLayout}
          >
            <Typography className={classes.text}>Save</Typography>
          </Button>
        </React.Fragment>
      ) : (
        <Button
          variant={'outlined'}
          startIcon={<BorderColorOutlined style={{ marginTop: '1px', fontSize: '16px' }} />}
          className={classes.button}
          style={{
            backgroundColor: '#FCFCFC',
            border: '1px solid #DCDCDC',
            color: theme.color.grey.neutral300,
          }}
          onClick={() => setEditLayout(!editLayout)}
        >
          <Typography className={classes.text}>Edit</Typography>
        </Button>
      )}
      {canEdit && (
        <Button
          startIcon={<AddCircle style={{ marginTop: '-1px', fontSize: '16px' }} />}
          className={classes.button}
          style={{
            backgroundColor: theme.color.secondary.main900,
            color: 'white',
          }}
          onClick={() => setShowEditWidget(true)}
        >
          <Typography className={classes.text}>Add Widget</Typography>
        </Button>
      )}
    </Box>
  );
};
